import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";

const NAV = [
  { label: "Home", href: "/#home" },
  { label: "Services", href: "/#services" },
  { label: "Why Us", href: "/#why" },
  { label: "Coverage", href: "/#coverage" },
  { label: "Reviews", href: "/reviews" },
  { label: "FAQ", href: "/#faq" },
  { label: "Quote", href: "/#quote" },
  { label: "Contact", href: "/#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[var(--jb-bone)]/85 backdrop-blur-md border-b border-[var(--jb-line)]"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-[88px] md:h-[104px]">
        <a href="#home" className="flex items-center gap-4" data-testid="logo-link" aria-label="Jaybliss Logistics — home">
          <picture>
            <source srcSet="/logo-jll.webp" type="image/webp" />
            <img
              src="/logo-jll.png"
              alt=""
              className="h-14 md:h-[72px] w-auto object-contain"
              width="228"
              height="240"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
          <div className="leading-none">
            <div className="text-[13px] sm:text-[15px] md:text-[19px] font-extrabold tracking-[0.14em] text-[var(--jb-navy)] whitespace-nowrap">
              JAYBLISS
            </div>
            <div className="text-[13px] sm:text-[15px] md:text-[19px] font-extrabold tracking-[0.14em] text-[var(--jb-navy)] mt-[3px] whitespace-nowrap">
              LOGIS<span className="text-[var(--jb-rust)]">TICS</span>
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.label}
              href={n.href}
              className="mono-cap link-underline text-[var(--jb-navy)]"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <div
            className="flex items-center gap-2 text-[var(--jb-navy)] border border-[var(--jb-line)] px-3 py-1.5"
            data-testid="insured-badge"
          >
            <ShieldCheck className="w-4 h-4 text-[var(--jb-rust)]" strokeWidth={1.5} />
            <span className="mono-cap">Fully Insured</span>
          </div>
          <a
            href="#quote"
            className="cta-primary bg-[var(--jb-navy)] text-[var(--jb-bone)] mono-cap px-5 py-3"
            data-testid="header-cta-quote"
          >
            Get a Free Quote
          </a>
        </div>

        <button
          className="lg:hidden text-[var(--jb-navy)]"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-[var(--jb-bone)] border-b border-[var(--jb-line)] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="serif text-2xl text-[var(--jb-navy)]"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="tel:+447396764997"
                className="mt-4 inline-flex items-center gap-2 mono-cap text-[var(--jb-rust)]"
                data-testid="mobile-call"
              >
                <Phone className="w-4 h-4" /> +44 7396 764997
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
